// @ts-nocheck
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { AgGridReact } from '@ag-grid-community/react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import http from '../../service/axiosConfig'
import {
  API_DRAMATIQ_TASK_RESULT,
  API_EQUIPAGE_STATS,
  API_EQUIPAGE_STATS_REFRESH,
} from '../../service/api.endpoint'
import './equipageStats.css'


function PageEquipageStats() {
  const gridRef = useRef(null)
  const [rows, setRows] = useState([])
  const [totals, setTotals] = useState(null)
  const [deviceTotals, setDeviceTotals] = useState(null)
  const [orgTotals, setOrgTotals] = useState([])
  const [locationTotals, setLocationTotals] = useState([])
  const [locationChoices, setLocationChoices] = useState([])
  const [performance, setPerformance] = useState(null)
  const [contractorPerformance, setContractorPerformance] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [selectedEquipage, setSelectedEquipage] = useState(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    document.title = 'АСПО.Контроль | Экипажи'
    loadRows()
  }, [])

  async function loadRows() {
    setIsLoading(true)
    setLoadError('')
    try {
      const response = await http.post(API_EQUIPAGE_STATS_REFRESH)
      pollStatsTask(response.data.requestId)
    } catch (error) {
      console.error(error)
      setIsLoading(false)
      setLoadError(error.response?.data?.detail || 'Не удалось запустить обновление статистики')
    }
  }

  async function pollStatsTask(requestId) {
    try {
      const response = await http.get(API_DRAMATIQ_TASK_RESULT, {
        params: { requestId },
      })
      const task = response.data

      if (!task) {
        window.setTimeout(() => pollStatsTask(requestId), 1500)
        return
      }

      if (task.status === 'ERROR') {
        setIsLoading(false)
        setLoadError(task.error || 'Ошибка фоновой задачи статистики экипажей')
        return
      }

      if (task.status === 'DONE') {
        applyPayload(task.data || {})
        setIsLoading(false)
        return
      }

      window.setTimeout(() => pollStatsTask(requestId), 1500)
    } catch (error) {
      console.error(error)
      setIsLoading(false)
      setLoadError(error.response?.data?.detail || 'Не удалось получить результат обновления')
    }
  }

  function applyPayload(payload) {
    setRows(payload.results || [])
    setTotals(payload.totals || null)
    setDeviceTotals(payload.device_totals || null)
    setOrgTotals(payload.org_totals || [])
    setLocationTotals(payload.location_totals || [])
    setLocationChoices(payload.location_choices || [])
    setPerformance(payload.performance || null)
    setContractorPerformance(payload.contractor_performance || null)
  }

  async function saveEquipageActive(active) {
    if (!selectedEquipage?.id || isSaving) return

    await saveEquipage({ active })
  }

  async function saveEquipageLocation(location) {
    if (!selectedEquipage?.id || isSaving) return

    await saveEquipage({ location })
  }

  async function saveEquipage(payload) {
    setIsSaving(true)
    try {
      const response = await http.patch(
        `${API_EQUIPAGE_STATS}/${selectedEquipage.id}/`,
        payload,
      )
      setSelectedEquipage(response.data)
      await loadRows()
    } catch (error) {
      console.error(error)
      alert(JSON.stringify(error.response?.data || { detail: 'Не удалось сохранить экипаж' }, null, 2))
    } finally {
      setIsSaving(false)
    }
  }

  const columnDefs = useMemo(() => [
    {
      field: 'org_name',
      headerName: 'Подрядчик',
      rowGroup: true,
      hide: true,
      enableRowGroup: true,
    },
    {
      field: 'name',
      headerName: 'Экипаж',
      minWidth: 140,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'active_display',
      headerName: 'Статус',
      width: 125,
      filter: 'agTextColumnFilter',
      cellClass: (params) => params.data?.active ? 'equipage-status-active' : 'equipage-status-inactive',
    },
    {
      field: 'location_display',
      headerName: 'Локация',
      width: 115,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'auto_bsi_upload_display',
      headerName: 'Авто БСИ',
      width: 120,
      filter: 'agTextColumnFilter',
      cellClass: (params) => params.data?.auto_bsi_upload ? 'equipage-auto-yes' : 'equipage-auto-no',
    },
    {
      field: 'bsi_work_status_display',
      headerName: 'БСИ',
      width: 125,
      filter: 'agTextColumnFilter',
      cellClass: (params) => params.data?.bsi_work_status === 'REPAIR' ? 'equipage-bsi-repair' : 'equipage-bsi-work',
    },
    {
      field: 'bsi_device_name',
      headerName: 'Прибор БСИ',
      minWidth: 170,
      flex: 1,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'bsi_device_imei',
      headerName: 'IMEI',
      width: 150,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'repair_date_from',
      headerName: 'Ремонт с',
      width: 115,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'repair_date_to',
      headerName: 'Ремонт по',
      width: 115,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'repair_comment',
      headerName: 'Причина ремонта',
      minWidth: 170,
      flex: 1,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'smena_time',
      headerName: 'Смена',
      width: 100,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => params.value || '—',
    },
  ], [])

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: false,
    floatingFilter: false,
  }), [])

  const autoGroupColumnDef = useMemo(() => ({
    headerName: 'Подрядчик',
    minWidth: 250,
    pinned: 'left',
    cellRendererParams: {
      suppressCount: false,
    },
  }), [])

  const onFirstDataRendered = useCallback(() => {
    gridRef.current?.api?.sizeColumnsToFit()
  }, [])

  return (
    <div className="equipage-stats-page">
      <div className="equipage-stats-topbar">
        <div>
          <h2 className="equipage-stats-title">Экипажи</h2>
          <div className="equipage-stats-subtitle">Статистика по активным экипажам и автоматической загрузке БСИ</div>
        </div>
        <button type="button" className="equipage-stats-button" onClick={loadRows} disabled={isLoading}>
          {isLoading ? 'Обновляем...' : 'Обновить'}
        </button>
      </div>

      <div className="equipage-stats-counters">
        <Counter label="Всего" value={rows.length} />
        <Counter label="Активные" value={totals?.active || 0} accent="green" />
        <Counter label="Не активные" value={totals?.inactive || 0} accent="gray" />
        <Counter label="Авто БСИ" value={totals?.auto_bsi_upload || 0} accent="blue" />
        <Counter label="Без авто БСИ" value={totals?.manual_bsi_upload || 0} accent="yellow" />
        <Counter label="БСИ в работе" value={totals?.bsi_work || 0} accent="green" />
        <Counter label="БСИ в ремонте" value={totals?.bsi_repair || 0} accent="red" />
        <Counter label="Приборов с IMEI" value={deviceTotals?.with_imei || 0} accent="blue" />
        <Counter label="Привязано к экипажам" value={deviceTotals?.linked_to_equipage || 0} accent="yellow" />
      </div>

      <div className="equipage-location-summary">
        <div className="equipage-location-summary-title">Активные экипажи по локациям</div>
        <div className="equipage-location-summary-list">
          {locationTotals.map((item) => (
            <div key={item.location || 'empty'} className="equipage-location-chip">
              <span>{item.location_display}</span>
              <strong>{item.active}</strong>
            </div>
          ))}
        </div>
      </div>

      {loadError && <div className="equipage-stats-error">{loadError}</div>}

      <EquipagePerformanceDashboard
        performance={performance}
        contractorPerformance={contractorPerformance}
      />

      <div className="equipage-stats-layout">
        <div className="ag-theme-alpine equipage-stats-grid">
          <AgGridReact
            ref={gridRef}
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            autoGroupColumnDef={autoGroupColumnDef}
            groupDisplayType="singleColumn"
            groupDefaultExpanded={1}
            rowGroupPanelShow="always"
            animateRows={true}
            rowHeight={28}
            headerHeight={30}
            getRowId={(params) => params.data.id}
            onFirstDataRendered={onFirstDataRendered}
            onRowClicked={(params) => {
              if (params.node?.group) return
              setSelectedEquipage(params.data)
            }}
          />
        </div>

        <div className="equipage-org-summary">
          <div className="equipage-org-summary-title">По подрядчикам</div>
          {orgTotals.map((item) => (
            <div key={item.org_name} className="equipage-org-summary-row">
              <strong>{item.org_name}</strong>
              <span>Всего: {item.total}</span>
              <span>Активные: {item.active}</span>
              <span>Авто БСИ: {item.auto_bsi_upload}</span>
              <span>БСИ в ремонте: {item.bsi_repair}</span>
            </div>
          ))}
        </div>
      </div>

      {selectedEquipage && (
        <EquipageDetailsModal
          equipage={selectedEquipage}
          saving={isSaving}
          locationChoices={locationChoices}
          onClose={() => setSelectedEquipage(null)}
          onToggleActive={saveEquipageActive}
          onChangeLocation={saveEquipageLocation}
        />
      )}
    </div>
  )
}

function EquipagePerformanceDashboard({ performance, contractorPerformance }) {
  const monthly = contractorPerformance?.monthly || []
  const contractors = contractorPerformance?.contractors || []
  const leader = contractorPerformance?.leader
  const topEquipages = performance?.top_equipages || []
  const maxContractorPlan = Math.max(...contractors.map((item) => item.current_plan || 0), 1)

  return (
    <div className="equipage-dashboard">
      <div className="equipage-dashboard-header">
        <div>
          <h3>Закрытие фонда подрядчиками</h3>
          <span>
            Плановые скважины / закрытые скважины за {contractorPerformance?.period_title || 'текущий и предыдущий год'}.
            Текущий месяц: {contractorPerformance?.current_month_label || '—'}
          </span>
        </div>
        {leader && (
          <div className="equipage-dashboard-leader">
            <span>Лучшее закрытие месяца</span>
            <strong>{leader.org_name}</strong>
            <em>{leader.current_done} скв. из {leader.current_plan} / {leader.current_percent}%</em>
          </div>
        )}
      </div>

      <div className="equipage-dashboard-grid">
        <div className="equipage-dashboard-card equipage-dashboard-card-wide">
          <div className="equipage-dashboard-card-title">Закрытие фонда по месяцам</div>
          <div className="equipage-chart-box">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthly.map((item) => ({ ...item, label: formatMonth(item.month) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d9e2ef" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={1} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={chartTooltipFormatter} labelFormatter={(value) => `Месяц: ${value}`} />
                <Legend />
                <Bar
                  dataKey="done"
                  name="Закрыто скважин"
                  fill="#2e7d32"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="plan"
                  name="Плановый фонд, скв."
                  fill="#90caf9"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="equipage-dashboard-card">
          <div className="equipage-dashboard-card-title">Подрядчики за текущий месяц</div>
          <div className="equipage-top-list">
            {contractors.length === 0 && <div className="equipage-empty">Нет данных за период</div>}
            {contractors.map((item, index) => (
              <div key={item.org_name} className="equipage-top-row">
                <div className="equipage-top-row-head">
                  <strong>{index + 1}. {item.org_name}</strong>
                  <span>{item.current_percent}%</span>
                </div>
                <div className="equipage-top-row-org">
                  Закрыто {item.current_done} скв. из {item.current_plan}
                </div>
                <div className="equipage-bar-track">
                  <div
                    className="equipage-bar-fill"
                    style={{ width: `${Math.max(6, (item.current_plan / maxContractorPlan) * 100)}%` }}
                  />
                </div>
                <div className="equipage-top-row-foot">
                  Осталось: {item.current_missed}
                  <span>Всего за период: {item.done}/{item.plan}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="equipage-dashboard-card">
          <div className="equipage-dashboard-card-title">Топ экипажей по проходкам</div>
          <div className="equipage-chart-box compact">
            <div className="equipage-top-list">
              {topEquipages.length === 0 && <div className="equipage-empty">Нет данных за период</div>}
              {topEquipages.slice(0, 8).map((item, index) => (
                <div key={item.equipage_id} className="equipage-top-row">
                  <div className="equipage-top-row-head">
                    <strong>{index + 1}. {item.equipage}</strong>
                    <span>{item.full_passes}</span>
                  </div>
                  <div className="equipage-top-row-org">{item.org_name}</div>
                  <div className="equipage-top-row-foot">
                    Подходов: {item.total_passes}
                    <span>Макс: {Math.round(item.max_depth || 0)} м</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function Counter({ label, value, accent = '' }) {
  return (
    <div className={`equipage-counter ${accent ? `is-${accent}` : ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function formatDate(value) {
  if (!value) return '—'
  return String(value).slice(0, 10)
}

function formatMonth(value) {
  if (!value) return '—'
  const [year, month] = String(value).split('-')
  return `${month}.${year}`
}

function chartTooltipFormatter(value, name) {
  return [value, name]
}

function EquipageDetailsModal({ equipage, saving, locationChoices, onClose, onToggleActive, onChangeLocation }) {
  return (
    <div className="equipage-modal-overlay">
      <div className="equipage-modal">
        <div className="equipage-modal-header">
          <div>
            <h3>Экипаж {equipage.name}</h3>
            <span>{equipage.org_name}</span>
          </div>
          <button type="button" onClick={onClose} disabled={saving}>×</button>
        </div>

        <div className="equipage-modal-details">
          <Info label="Статус" value={equipage.active_display} accent={equipage.active ? 'green' : 'gray'} />
          <label className="equipage-modal-info edit">
            <span>Локация</span>
            <select
              value={equipage.location || ''}
              onChange={(event) => onChangeLocation(event.target.value)}
              disabled={saving}
            >
              <option value="">Не указана</option>
              {locationChoices.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </label>
          <Info label="БСИ" value={equipage.bsi_work_status_display} accent={equipage.bsi_work_status === 'REPAIR' ? 'red' : 'green'} />
          <Info label="Авто БСИ" value={equipage.auto_bsi_upload_display} accent={equipage.auto_bsi_upload ? 'blue' : 'gray'} />
          <Info label="Прибор" value={equipage.bsi_device_name} />
          <Info label="IMEI" value={equipage.bsi_device_imei} />
          <Info label="Смена" value={equipage.smena_time} />
          <Info label="Ремонт с" value={formatDate(equipage.repair_date_from)} />
          <Info label="Ремонт по" value={formatDate(equipage.repair_date_to)} />
          <Info label="Причина ремонта" value={equipage.repair_comment} />
        </div>

        {!equipage.bsi_device_imei && (
          <div className="equipage-modal-note">
            У экипажа не указан IMEI прибора, автоматическая загрузка БСИ по нему не будет определяться.
          </div>
        )}

        <div className="equipage-modal-actions">
          <button
            type="button"
            className={equipage.active ? 'danger' : 'success'}
            onClick={() => onToggleActive(!equipage.active)}
            disabled={saving}
          >
            {saving ? 'Сохраняем...' : equipage.active ? 'Сделать не активным' : 'Вернуть в активные'}
          </button>
          <button type="button" onClick={onClose} disabled={saving}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  )
}

function Info({ label, value, accent = '' }) {
  return (
    <div className="equipage-modal-info">
      <span>{label}</span>
      <strong className={accent ? `is-${accent}` : ''}>{value || '—'}</strong>
    </div>
  )
}

export default PageEquipageStats
